import { useState } from 'react'
import { Image as ImageIcon, Type, Palette } from 'lucide-react'

export function ControlPanel({ cardData, setCardData }) {
  const [activeTab, setActiveTab] = useState('front')

  const updateCardData = (field, value) => {
    setCardData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="space-y-4">
      {/* Card Side Tabs */}
      <div className="glass rounded-lg p-1 flex gap-1">
        <button
          onClick={() => {
            setActiveTab('front')
            updateCardData('currentSide', 'front')
          }}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'front'
              ? 'bg-primary text-primary-foreground shadow-lg'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Front
        </button>
        <button
          onClick={() => {
            setActiveTab('back')
            updateCardData('currentSide', 'back')
          }}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'back'
              ? 'bg-primary text-primary-foreground shadow-lg'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Back (Upgraded)
        </button>
      </div>

      {/* Card Properties Section */}
      <div className="glass rounded-xl p-6 space-y-6 custom-scrollbar max-h-[calc(100vh-16rem)] overflow-y-auto">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Type className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Card Properties</h2>
          </div>

          <div className="space-y-4">
            {/* Card Name */}
            <div className="space-y-2">
              <label htmlFor="cardName" className="text-sm font-medium text-foreground">
                Card Name
              </label>
              <input
                id="cardName"
                type="text"
                value={cardData.name}
                onChange={(e) => updateCardData('name', e.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-card px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition-all"
                placeholder="Enter card name"
                maxLength={30}
              />
            </div>

            {/* Energy Cost */}
            <div className="space-y-2">
              <label htmlFor="energyCost" className="text-sm font-medium text-foreground">
                Energy Cost
              </label>
              <input
                id="energyCost"
                type="number"
                value={cardData.energyCost}
                onChange={(e) => updateCardData('energyCost', parseInt(e.target.value))}
                className="flex h-10 w-full rounded-lg border border-input bg-card px-4 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition-all"
                min={0}
                max={10}
              />
            </div>

            {/* Card Text */}
            <div className="space-y-2">
              <label htmlFor="cardText" className="text-sm font-medium text-foreground">
                Card Text
              </label>
              <textarea
                id="cardText"
                value={cardData.description}
                onChange={(e) => updateCardData('description', e.target.value)}
                className="flex min-h-[80px] w-full rounded-lg border border-input bg-card px-4 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition-all resize-none"
                placeholder="Enter card description"
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground">
                Description of what the card does
              </p>
            </div>

            {/* Type and Color */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="cardType" className="text-sm font-medium text-foreground">
                  Type
                </label>
                <select
                  id="cardType"
                  value={cardData.type}
                  onChange={(e) => updateCardData('type', e.target.value)}
                  className="flex h-10 w-full rounded-lg border border-input bg-card px-4 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition-all"
                >
                  <option value="Attack">Attack</option>
                  <option value="Skill">Skill</option>
                  <option value="Power">Power</option>
                </select>
              </div>

              <div className="space-y-2">
                <label htmlFor="cardColor" className="text-sm font-medium text-muted-foreground">
                  Color <span className="text-xs">(Coming Soon)</span>
                </label>
                <select
                  id="cardColor"
                  value={cardData.color}
                  onChange={(e) => updateCardData('color', e.target.value)}
                  className="flex h-10 w-full rounded-lg border border-input bg-muted px-4 py-2 text-sm text-muted-foreground cursor-not-allowed opacity-60"
                  disabled
                >
                  <option value="Red">Red</option>
                  <option value="Green">Green</option>
                  <option value="Blue">Blue</option>
                  <option value="Purple">Purple</option>
                  <option value="Colorless">Colorless</option>
                </select>
              </div>
            </div>

            {/* Rarity */}
            <div className="space-y-2">
              <label htmlFor="cardRarity" className="text-sm font-medium text-muted-foreground">
                Rarity <span className="text-xs">(Coming Soon)</span>
              </label>
              <select
                id="cardRarity"
                value={cardData.rarity}
                onChange={(e) => updateCardData('rarity', e.target.value)}
                className="flex h-10 w-full rounded-lg border border-input bg-muted px-4 py-2 text-sm text-muted-foreground cursor-not-allowed opacity-60"
                disabled
              >
                <option value="Common">Common</option>
                <option value="Uncommon">Uncommon</option>
                <option value="Rare">Rare</option>
              </select>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Artwork Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Artwork</h2>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Upload Card Art
            </label>
            <div className="relative rounded-xl border-2 border-dashed border-border p-8 text-center glass hover:border-primary hover:bg-primary/5 transition-all cursor-pointer">
              <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-sm text-foreground mb-2">
                Drag & drop your image here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG up to 5MB
              </p>
              <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                accept="image/png,image/jpeg"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  if (file) {
                    try {
                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        alert('Please select an image file (PNG or JPG)')
                        return
                      }

                      // Validate file size (5MB max)
                      const MAX_SIZE = 5 * 1024 * 1024
                      if (file.size > MAX_SIZE) {
                        alert('File size must be less than 5MB')
                        return
                      }

                      // Create object URL and load image
                      const imageUrl = URL.createObjectURL(file)
                      const img = new Image()

                      img.onload = () => {
                        console.log('✓ Artwork loaded:', img.width, 'x', img.height)
                        // Update card data with loaded image
                        updateCardData('artwork', img)
                        // Clean up the object URL after a delay
                        setTimeout(() => URL.revokeObjectURL(imageUrl), 100)
                      }

                      img.onerror = () => {
                        console.error('Failed to load image')
                        alert('Failed to load image. Please try another file.')
                        URL.revokeObjectURL(imageUrl)
                      }

                      img.src = imageUrl
                    } catch (error) {
                      console.error('Error loading artwork:', error)
                      alert('Error loading image. Please try again.')
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
